/*
 * JHR upgraded "Added to cart" notification - v2 (2026-06-16)
 * ------------------------------------------------------------------
 * Theme: Symmetry (Clean Canvas) 8.3.1 - Just Horse Riders
 * DROP-IN REPLACEMENT for assets/added-notification.js.
 *
 * v2 fixes the layout collision found in v1: the theme's main.css sets
 *     .added-notification { display: flex }      (the old single-row toast)
 * which made the new stacked sections render side-by-side. We now add a
 * unique class `jhr-anv2` to the root and set display:block at higher
 * specificity (0,2,0) so our layout wins (no !important needed - verified the
 * theme uses none on these rules). We deliberately DO NOT override the theme's
 * position/z-index: it drops the card neatly below the sticky header (top
 * right) at z-index 400, which is BELOW the fly-to-cart clone's 99998, so the
 * flying product image always stays on top.
 *
 * Invocation contract UNCHANGED: main.js still clones #AddedNotification and
 * sets dataset.productTitle; we keep the custom element name + read it, so no
 * edit to main.js / theme.liquid / the template is required.
 *
 * Rich data (image, variant, cart count, and cart total for the free-delivery
 * nudge) comes from one GET to /cart.js. We match the just-added line by
 * product_title (Shopify merges a re-added item into its EXISTING line, so
 * "last item" is unreliable). Degrades to a clean title-only card on failure.
 *
 * Fly-to-cart (snippets/jhr-slide-effects.liquid) is left completely
 * untouched; the card reveals the instant /cart.js settles (snappy).
 *
 * Brand: #38B6FF accent + #0277BD primary button (white text passes contrast;
 * #38B6FF would not). Owner is colour-blind: success is shown by a tick icon +
 * the words "Added to cart" (never colour alone); the free-ship state always
 * carries a text label, not just a bar colour. File is pure ASCII (currency
 * and tick use \u escapes) to avoid any encoding mangling.
 */

if (!customElements.get('added-notification')) {
  const JHR_AN_STYLE_ID = 'jhr-added-notification-styles-v2';
  if (!document.getElementById(JHR_AN_STYLE_ID)) {
    const style = document.createElement('style');
    style.id = JHR_AN_STYLE_ID;
    style.textContent = `
/* Root - scoped with .jhr-anv2 so it beats the theme's .added-notification{display:flex} */
.added-notification.jhr-anv2 {
  display: block;
  box-sizing: border-box;
  width: 370px;
  max-width: min(380px, calc(100vw - 24px));
  padding: 0;
  background: #ffffff;
  color: #14181c;
  border: 1px solid #e4e8ec;
  border-radius: 14px;
  box-shadow: 0 12px 34px rgba(20, 24, 28, 0.18), 0 2px 6px rgba(20, 24, 28, 0.08);
  overflow: hidden;
  font-family: inherit;
  line-height: 1.4;
  transform: translateY(-10px);
  transition: opacity 0.28s ease, transform 0.4s cubic-bezier(0.2, 0.8, 0.25, 1);
}
.added-notification.jhr-anv2.added-notification--hidden { opacity: 0; transform: translateY(-10px); visibility: hidden; }
.added-notification.jhr-anv2:not(.added-notification--hidden):not(.added-notification--dismissed) { opacity: 1; transform: translateY(0); visibility: visible; }
/*
 * THE EXIT HAS ITS OWN CURVE — it used to borrow the entrance's.
 *
 * Arriving and leaving are not the same gesture. The entrance is a quick
 * decelerating drop, which is right for something demanding attention. Reusing
 * it for the exit gave a 0.28s fade across 10px, which read as the card being
 * switched off rather than leaving. Slower, a little more travel, and a touch
 * of scale so it recedes instead of blinking out.
 *
 * Both properties here are opacity and transform, so the compositor runs this
 * on its own thread and the exit stays smooth even while the page is busy —
 * the same reason the countdown ring is built from rotation.
 */
.added-notification.jhr-anv2.added-notification--dismissed {
  opacity: 0;
  transform: translateY(-22px) scale(0.96);
  /*
   * NOT an "ease-out" curve. The first attempt used cubic-bezier(0.33,1,0.68,1)
   * and a frame-by-frame trace showed why the owner still called it a snap:
   * opacity went 1 -> 0.48 in the first 84ms and then spent 200ms finishing
   * invisibly. Front-loading the change is exactly what makes something look
   * switched off rather than leaving.
   *
   * cubic-bezier(0.4, 0, 0.2, 1) spreads the change evenly, and the travel is
   * up from 14px to 22px with a deeper scale, because at this size movement
   * reads as "going" far more than opacity does.
   */
  transition: opacity 0.45s cubic-bezier(0.4, 0, 0.2, 1), transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
}
@media (prefers-reduced-motion: reduce) {
  .added-notification.jhr-anv2 { transition: opacity 0.2s ease; transform: none; }
  /* Same specificity as the rule above, later in source, so it wins. */
  .added-notification.jhr-anv2.added-notification--dismissed { transform: none; transition: opacity 0.2s ease; }
}

.jhr-anv2 .jhr-an__bar { height: 4px; background: #38B6FF; }

.jhr-anv2 .jhr-an__head { display: flex; align-items: center; gap: 9px; padding: 13px 14px 10px; }
.jhr-anv2 .jhr-an__tick { flex: 0 0 auto; width: 22px; height: 22px; color: #0277BD; display: inline-flex; align-items: center; justify-content: center; }
.jhr-anv2 .jhr-an__tick svg { width: 22px; height: 22px; display: block; }
.jhr-anv2 .jhr-an__headline { flex: 1 1 auto; font-weight: 700; font-size: 15px; color: #14181c; letter-spacing: -0.01em; }
.jhr-anv2 .jhr-an__close { flex: 0 0 auto; width: 30px; height: 30px; padding: 0; border: 0; background: transparent; color: #5a636b; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; line-height: 0; }
.jhr-anv2 .jhr-an__close:hover { background: #f1f4f6; color: #14181c; }
.jhr-anv2 .jhr-an__close:focus-visible { outline: 2px solid #0277BD; outline-offset: 2px; }
.jhr-anv2 .jhr-an__close svg.jhr-an__x { width: 14px; height: 14px; display: block; }

/* ---- countdown ring around the close button -------------------------------
 * Tells the shopper how long the card has left, so it never just vanishes
 * mid-read. It depletes rather than fills: a shrinking arc reads as "running
 * out" without needing a label.
 *
 * ⚠ WHY THIS IS BUILT OUT OF ROTATING HALVES AND NOT AN SVG stroke-dashoffset
 *
 * The obvious way to draw this is one SVG circle animating stroke-dashoffset.
 * That was the first version and it was WRONG for this page: dashoffset is not
 * a compositable property, so it animates on the MAIN THREAD. Add to cart
 * shortly after opening a tab — while Boost, Klaviyo and Clarity are still
 * running, roughly 1.9s of main-thread work between them — and the ring
 * visibly froze, then jumped back into motion. The owner reported exactly
 * that, twice, and he was right both times.
 *
 * "transform" IS compositable. An animation built only from rotation keeps
 * running on the compositor thread even while the main thread is blocked, so
 * the ring cannot stall no matter what else the page is doing.
 *
 * HOW IT WORKS. A blue ring sits underneath. Two grey half-rings rotate over
 * it to cover it up, one per half of the duration:
 *   - the RIGHT window holds a cover clipped to its LEFT half. At 0deg that
 *     cover sits over on the left, out of the window and invisible; rotating
 *     it to 180deg sweeps it across the right side, 12 o'clock round to 6.
 *   - the LEFT window holds the mirror image, delayed by half the duration, so
 *     it carries on from 6 o'clock back round to 12.
 * Each window is "overflow: hidden", which is what stops a cover appearing in
 * the half it does not belong to.
 */
.jhr-anv2 .jhr-an__close { position: relative; }
.jhr-anv2 .jhr-an__ring { position: absolute; top: 0; left: 0; width: 30px; height: 30px; pointer-events: none; }
/* The blue ring that gets covered up. */
.jhr-anv2 .jhr-an__ring::before {
  content: ''; position: absolute; top: 0; left: 0; width: 30px; height: 30px;
  box-sizing: border-box; border: 2px solid #0277BD; border-radius: 50%; opacity: 0;
}
.jhr-anv2.jhr-an--counting .jhr-an__ring::before { opacity: 1; }
.jhr-anv2.jhr-an--paused .jhr-an__ring::before { border-color: #9aa4ac; }

.jhr-anv2 .jhr-an__ring-half { position: absolute; top: 0; width: 15px; height: 30px; overflow: hidden; }
.jhr-anv2 .jhr-an__ring-half--r { right: 0; }
.jhr-anv2 .jhr-an__ring-half--l { left: 0; }

.jhr-anv2 .jhr-an__ring-cover {
  position: absolute; top: 0; width: 30px; height: 30px; box-sizing: border-box;
  border: 2px solid #e4e8ec; border-radius: 50%;
  transform: rotate(0deg); transform-origin: 50% 50%;
}
.jhr-anv2 .jhr-an__ring-half--r .jhr-an__ring-cover { left: -15px; clip-path: inset(0 50% 0 0); }
.jhr-anv2 .jhr-an__ring-half--l .jhr-an__ring-cover { left: 0; clip-path: inset(0 0 0 50%); }

.jhr-anv2.jhr-an--counting .jhr-an__ring-half--r .jhr-an__ring-cover {
  animation: jhr-an-sweep calc(var(--jhr-an-ms, 5000ms) / 2) linear forwards;
}
.jhr-anv2.jhr-an--counting .jhr-an__ring-half--l .jhr-an__ring-cover {
  animation: jhr-an-sweep calc(var(--jhr-an-ms, 5000ms) / 2) linear calc(var(--jhr-an-ms, 5000ms) / 2) forwards;
}
.jhr-anv2.jhr-an--paused .jhr-an__ring-cover { animation-play-state: paused; }

@keyframes jhr-an-sweep { from { transform: rotate(0deg); } to { transform: rotate(180deg); } }

/* A sweeping arc is decoration; the information is "time is running out".
 * Step it twice a second so that meaning survives without continuous motion. */
@media (prefers-reduced-motion: reduce) {
  .jhr-anv2.jhr-an--counting .jhr-an__ring-cover { animation-timing-function: steps(5); }
}

.jhr-anv2 .jhr-an__body { display: flex; gap: 12px; align-items: center; padding: 2px 14px 12px; }
.jhr-anv2 .jhr-an__thumb { flex: 0 0 auto; width: 58px; height: 58px; border-radius: 10px; border: 1px solid #e4e8ec; background: #f6f8f9; overflow: hidden; }
.jhr-anv2 .jhr-an__thumb[hidden] { display: none; }
.jhr-anv2 .jhr-an__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.jhr-anv2 .jhr-an__info { flex: 1 1 auto; min-width: 0; }
.jhr-anv2 .jhr-an__title { font-weight: 600; font-size: 14px; color: #14181c; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.jhr-anv2 .jhr-an__variant { margin-top: 2px; font-size: 12.5px; color: #5a636b; }
.jhr-anv2 .jhr-an__variant[hidden] { display: none; }

/* Free UK delivery nudge */
.jhr-anv2 .jhr-an__ship { padding: 11px 14px; background: #f4f9fd; border-top: 1px solid #eef2f5; border-bottom: 1px solid #eef2f5; }
.jhr-anv2 .jhr-an__ship[hidden] { display: none; }
.jhr-anv2 .jhr-an__ship-text { font-size: 12.5px; font-weight: 600; color: #14181c; display: flex; align-items: center; gap: 7px; }
.jhr-anv2 .jhr-an__ship-text svg { width: 16px; height: 16px; flex: 0 0 auto; color: #0277BD; }
.jhr-anv2 .jhr-an__ship-amount { color: #0277BD; }
.jhr-anv2 .jhr-an__track { margin-top: 8px; height: 6px; border-radius: 999px; background: #d4e4f0; overflow: hidden; }
.jhr-anv2 .jhr-an__fill { height: 100%; width: 0; background: #38B6FF; border-radius: 999px; transition: width 0.5s ease; }
.jhr-anv2 .jhr-an__ship--met .jhr-an__fill { background: #0277BD; }

.jhr-anv2 .jhr-an__actions { display: flex; gap: 9px; padding: 12px 14px 14px; }
.jhr-anv2 .jhr-an__btn { flex: 1 1 0; height: 44px; border-radius: 10px; font-size: 14px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; text-decoration: none; cursor: pointer; box-sizing: border-box; transition: background 0.15s ease, border-color 0.15s ease; }
.jhr-anv2 .jhr-an__btn--secondary { background: #ffffff; color: #14181c; border: 1.5px solid #14181c; }
.jhr-anv2 .jhr-an__btn--secondary:hover { background: #f1f4f6; }
.jhr-anv2 .jhr-an__btn--primary { background: #0277BD; color: #ffffff; border: 1.5px solid #0277BD; }
.jhr-anv2 .jhr-an__btn--primary:hover { background: #015f93; border-color: #015f93; }
.jhr-anv2 .jhr-an__btn:focus-visible { outline: 2px solid #0277BD; outline-offset: 2px; }
.jhr-anv2 .jhr-an__btn--primary:focus-visible { outline-color: #14181c; }
`;
    document.head.appendChild(style);
  }

  const FREE_SHIP_THRESHOLD = 5000; // pence - 50 GBP (theme setting free_shipping_amounts = GBP:5000)
  const REVEAL_DELAY = 600;          // FALLBACK only - we reveal the instant /cart.js settles (see _hydrate)
  /*
   * 5s of VISIBLE, ANIMATING time, shown as a ring around the close button.
   *
   * The card carries a Checkout button and the free-delivery nudge, so it has
   * to outlast a glance. The ring is what makes any timer safe: nothing
   * vanishes unannounced, and hovering, touching or tabbing into the card
   * stops the clock for as long as the shopper is engaged with it.
   *
   * Counted from the first PAINTED FRAME after reveal, not from when the
   * element is created — see _reveal(). Two separate things used to eat into
   * it: /cart.js hydration, and the main thread being blocked by other apps
   * during page load.
   *
   * One number, mirrored into CSS as --jhr-an-ms so the ring and the timer
   * cannot drift apart. Change it here and the ring follows.
   */
  const AUTO_DISMISS = 5000;
  // 2 Oct 2026: while an unanswered size question sits in the card ("Who's this for?" / "Is this for your horse?" -
  // assets/jhr-rider-save.js, jhr-horse-save.js) the one countdown runs longer, so there is time to read it. No pausing
  // and restarting (Aaron: the ring "about halfway through it resets itself").
  const AUTO_DISMISS_QUESTION = 10000;

  const fmtGBP = (pence) => '£' + (Math.max(0, pence) / 100).toFixed(2);

  /*
   * 2026-09-18 (Aaron: "there's a bit of a delay" after the fly): the card used
   * to fetch /cart.js AFTER Shopify confirmed the add, and only then show -
   * 100-200 ms of a ~1 s wait, on the critical path. Now a basket snapshot is
   * taken quietly at page load (and after every change), and the card is filled
   * in from that snapshot + the line Shopify just returned, so it shows the
   * instant the add is confirmed, fully populated. The real basket is still
   * fetched afterwards and any difference corrected silently. No snapshot
   * (fetch failed, or a stale one) = the old fetch-then-show path.
   */
  const SNAPSHOT_MAX_AGE = 10 * 60 * 1000;
  const snap = { cart: null, at: 0, pending: null };
  function cartUrlJs() { return ((window.theme && theme.routes && theme.routes.cart) || '/cart') + '.js'; }
  function refreshSnapshot() {
    if (snap.pending) { return snap.pending; }
    snap.pending = fetch(cartUrlJs(), { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => { if (cart) { snap.cart = cart; snap.at = Date.now(); } return cart; })
      .catch(() => null)
      .finally(() => { snap.pending = null; });
    return snap.pending;
  }
  const idle = ('requestIdleCallback' in window) ? (fn, o) => window.requestIdleCallback(fn, o) : (fn) => setTimeout(fn, 1200);
  if (document.readyState === 'complete') { idle(refreshSnapshot, { timeout: 4000 }); }
  else { window.addEventListener('load', () => idle(refreshSnapshot, { timeout: 4000 })); }
  let snapTimer = null;
  document.addEventListener('on:cart:change', () => { clearTimeout(snapTimer); snapTimer = setTimeout(refreshSnapshot, 800); });

  /** The basket as it must be now, from the snapshot plus the line Shopify just returned. Null if we cannot say. */
  function predictCart(added) {
    const c = snap.cart;
    if (!c || !added || Date.now() - snap.at > SNAPSHOT_MAX_AGE) { return null; }
    if (typeof c.item_count !== 'number' || typeof c.total_price !== 'number') { return null; }
    let prevQty = 0; let prevLine = 0;
    (c.items || []).forEach((it) => {
      const same = added.key ? it.key === added.key : it.variant_id === added.variant_id;
      if (same) { prevQty += it.quantity || 0; prevLine += it.final_line_price || 0; }
    });
    return {
      item_count: c.item_count - prevQty + (added.quantity || 0),
      total_price: c.total_price - prevLine + (added.final_line_price || 0),
      item: { product_title: added.product_title, variant_title: added.variant_title, image: added.image },
    };
  }

  function setHeaderCount(n) {
    if (typeof n !== 'number') { return; }
    document.querySelectorAll('.cart-link__count').forEach((el) => { el.textContent = String(n); });
  }

  const AddedNotification = class extends HTMLElement {
    connectedCallback() {
      this.classList.add('jhr-anv2');
      this.dismissOthers();
      this.setAttribute('role', 'status');
      this.setAttribute('aria-live', 'polite');
      this._build();
      this._setTitle(this.dataset.productTitle || '');
      this._hydrate();
      this._display();
    }

    disconnectedCallback() {
      clearTimeout(this.dismissTimer);
      clearTimeout(this.revealTimer);
      clearTimeout(this._startFallback);
      clearTimeout(this._removeTimer);
      if (this._esc) { document.removeEventListener('keydown', this._esc); }
    }

    dismissOthers() {
      document.querySelectorAll('.added-notification').forEach((n) => {
        if (n !== this && typeof n.dismiss === 'function') { n.dismiss(); }
      });
    }

    _build() {
      const cartUrl = (window.theme && theme.routes && theme.routes.cart) || '/cart';
      this.innerHTML = `
        <div class="jhr-an__bar" aria-hidden="true"></div>
        <div class="jhr-an__head">
          <span class="jhr-an__tick" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M7.5 12.5l3 3 6-6.5"></path></svg>
          </span>
          <span class="jhr-an__headline">Added to cart</span>
          <button type="button" class="jhr-an__close" aria-label="Close">
            <span class="jhr-an__ring" aria-hidden="true">
              <span class="jhr-an__ring-half jhr-an__ring-half--r"><i class="jhr-an__ring-cover"></i></span>
              <span class="jhr-an__ring-half jhr-an__ring-half--l"><i class="jhr-an__ring-cover"></i></span>
            </span>
            <svg class="jhr-an__x" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13"></path></svg>
          </button>
        </div>
        <div class="jhr-an__body">
          <span class="jhr-an__thumb" hidden></span>
          <div class="jhr-an__info">
            <div class="jhr-an__title"></div>
            <div class="jhr-an__variant" hidden></div>
          </div>
        </div>
        <div class="jhr-an__ship" hidden>
          <div class="jhr-an__ship-text"></div>
          <div class="jhr-an__track" aria-hidden="true"><div class="jhr-an__fill"></div></div>
        </div>
        <div class="jhr-an__actions">
          <a class="jhr-an__btn jhr-an__btn--secondary" href="${cartUrl}" rel="nofollow">View cart</a>
          <a class="jhr-an__btn jhr-an__btn--primary" href="/checkout">Checkout</a>
        </div>
      `;
    }

    _setTitle(t) {
      const el = this.querySelector('.jhr-an__title');
      if (el) { el.textContent = t || 'Item added'; }
    }

    _hydrate() {
      const want = (this.dataset.productTitle || '').trim();
      let added = null;
      try { added = this.dataset.added ? JSON.parse(this.dataset.added) : null; } catch (e) { added = null; }
      const guess = predictCart(added);

      if (guess) {
        // Fast path: show now, from what Shopify just returned + the snapshot; check afterwards.
        this._applyTotals(guess.item_count, guess.total_price);
        this._applyItem(guess.item);
        setHeaderCount(guess.item_count);
        this._reveal();
        refreshSnapshot().then((cart) => {
          if (!cart || !this.isConnected) { return; }
          if (cart.item_count !== guess.item_count || cart.total_price !== guess.total_price) {
            this._applyTotals(cart.item_count, cart.total_price);
            setHeaderCount(cart.item_count);
          }
        });
        return;
      }

      // Slow path (no usable snapshot): fetch, then show - as before.
      refreshSnapshot()
        .then((cart) => {
          if (!cart || !this.isConnected) { return; }
          this._applyTotals(cart.item_count, cart.total_price);
          setHeaderCount(cart.item_count);
          const items = cart.items || [];
          if (!items.length) { return; }
          let item = null;
          if (want) {
            for (let i = items.length - 1; i >= 0; i--) {
              if ((items[i].product_title || '').trim() === want) { item = items[i]; break; }
            }
          }
          if (!item) { item = items[items.length - 1]; }
          this._applyItem({ product_title: item.product_title, variant_title: item.variant_title, image: item.image || (item.featured_image && item.featured_image.url) });
        })
        .catch(() => { /* title-only card still shows */ })
        .finally(() => this._reveal());
    }

    _applyTotals(itemCount, totalPrice) {
      if (typeof itemCount === 'number') {
        const vc = this.querySelector('.jhr-an__btn--secondary');
        if (vc) { vc.textContent = `View cart (${itemCount})`; }
      }
      if (typeof totalPrice === 'number') { this._ship(totalPrice); }
    }

    _applyItem(item) {
      if (!item) { return; }
      if (item.product_title) { this._setTitle(item.product_title); }
      const raw = item.image;
      if (raw) {
        const thumb = this.querySelector('.jhr-an__thumb');
        if (thumb && !thumb.querySelector('img')) {
          const img = document.createElement('img');
          img.src = this._img(raw, 120);
          img.alt = item.product_title || '';
          img.decoding = 'async';
          thumb.appendChild(img);
          thumb.hidden = false;
        }
      }
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      if (variant) {
        const v = this.querySelector('.jhr-an__variant');
        if (v) { v.textContent = variant; v.hidden = false; }
      }
    }

    _ship(total) {
      const wrap = this.querySelector('.jhr-an__ship');
      const text = this.querySelector('.jhr-an__ship-text');
      const fill = this.querySelector('.jhr-an__fill');
      if (!wrap || !text || !fill) { return; }
      const truck = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h15v13H1zM16 8h4l3 3v5h-7"></path><circle cx="5.5" cy="18.5" r="1.8"></circle><circle cx="18.5" cy="18.5" r="1.8"></circle></svg>';
      const threshold = FREE_SHIP_THRESHOLD;
      if (total >= threshold) {
        wrap.classList.add('jhr-an__ship--met');
        text.innerHTML = truck + '<span><strong>FREE UK delivery</strong> ✓</span>';
        fill.style.width = '100%';
      } else {
        const remain = threshold - total;
        text.innerHTML = truck + '<span><span class="jhr-an__ship-amount">' + fmtGBP(remain) + '</span> away from <strong>FREE UK delivery</strong></span>';
        const pct = Math.max(4, Math.min(100, Math.round((total / threshold) * 100)));
        fill.style.width = pct + '%';
      }
      wrap.hidden = false;
    }

    _img(url, px) {
      try {
        let o = String(url);
        if (o.indexOf('//') === 0) { o = 'https:' + o; }
        if (/(\?|&)(width|height)=/.test(o)) { return o; }
        return o + (o.indexOf('?') === -1 ? '?' : '&') + 'width=' + px;
      } catch (e) {
        return url;
      }
    }

    _reveal() {
      if (this._revealed || !this.isConnected) { return; }
      this._revealed = true;
      clearTimeout(this.revealTimer);
      this.classList.remove('added-notification--hidden');
      /*
       * DO NOT START THE CLOCK UNTIL THE BROWSER IS ACTUALLY PAINTING.
       *
       * The ring animates stroke-dashoffset, which runs on the MAIN THREAD, so
       * it cannot draw a frame while something else is occupying it. On a cold
       * load this page hands roughly 1.9s of main-thread work to other apps
       * (Boost ~943ms, Klaviyo ~399ms, Clarity ~324ms). Add to cart during
       * that and the ring froze, then jumped — which is exactly what the owner
       * saw on his first visit.
       *
       * rAF callbacks only run when the browser gets to paint, so waiting for
       * two of them means we start counting once frames are genuinely flowing.
       * Two rather than one: the first can fire in the same frame the class
       * change lands, before the card has actually been painted.
       *
       * This does not make a mid-countdown stutter impossible — only fewer
       * long tasks can do that — but it stops the shopper being charged for
       * time the card was frozen.
       */
      const go = () => {
        if (this._started || !this.isConnected) { return; }
        this._started = true;
        clearTimeout(this._startFallback);
        this._start();
      };

      /*
       * ⚠ rAF IS THE PREFERENCE, NEVER THE ONLY PATH.
       *
       * requestAnimationFrame does not fire AT ALL in a background tab, and an
       * earlier version of this relied on it alone. The result was the worst
       * possible failure: the card appeared, the ring stayed blank, the
       * countdown never started and the card NEVER DISMISSED — it just sat
       * there. Caught on a real browser whose window was not OS-foreground.
       *
       * So a plain timer is the floor. On a visible tab rAF wins the race
       * within a frame or two and the timer is cleared; on a hidden one the
       * timer starts things anyway, which is exactly what this code did before
       * the paint-alignment was added.
       */
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(function () { requestAnimationFrame(go); });
      }
      this._startFallback = setTimeout(go, 250);
    }

    _display() {
      // Reveal the moment /cart.js settles (in _hydrate's .finally) so the card pops up
      // near-instantly AND fully populated. This timer is only a fallback if the fetch hangs.
      this.revealTimer = setTimeout(() => this._reveal(), REVEAL_DELAY);

      // NOT _start() here — _reveal() owns that, so the 3.5s is 3.5s of the
      // card being on screen rather than 3.5s from the fetch being kicked off.
      this.style.setProperty('--jhr-an-ms', AUTO_DISMISS + 'ms');

      this.addEventListener('mouseenter', () => this._pause());
      this.addEventListener('mouseleave', () => this._start());
      this.addEventListener('focusin', () => this._pause());
      this.addEventListener('focusout', () => this._start());
      /*
       * Touch has no hover, so on a phone the ring would run to zero under the
       * shopper's thumb. A touch anywhere on the card stops it, and nothing
       * restarts it — on mobile, deliberately touching the card is a clear
       * enough signal that they want to read it.
       */
      this.addEventListener('touchstart', () => this._pause(), { passive: true });

      const c = this.querySelector('.jhr-an__close');
      if (c) { c.addEventListener('click', this.dismiss.bind(this)); }

      this._esc = (e) => { if (e.key === 'Escape' || e.key === 'Esc') { this.dismiss(); } };
      document.addEventListener('keydown', this._esc);
    }

    _start() {
      // Nothing should be counting down while the card is still hidden.
      if (!this._revealed) { return; }
      clearTimeout(this.dismissTimer);
      const ms = this.querySelector('.jhr-hs:not(.jhr-hs--done)') ? AUTO_DISMISS_QUESTION : AUTO_DISMISS;
      this.style.setProperty('--jhr-an-ms', ms + 'ms');               // the ring runs for the same time
      this.dismissTimer = setTimeout(this.dismiss.bind(this), ms);
      /*
       * Restart the ring from full, to match the timer — coming back from a
       * pause gives the shopper the whole 3.5s again, so a ring resuming from
       * where it stopped would be lying about how long is left.
       *
       * Removing the class is not enough on its own: without forcing a reflow
       * between the remove and the add, the browser coalesces both into one
       * frame, sees no change, and the animation never restarts.
       */
      this.classList.remove('jhr-an--paused', 'jhr-an--counting');
      void this.offsetWidth;
      this.classList.add('jhr-an--counting');
    }

    _pause() {
      clearTimeout(this.dismissTimer);
      this.classList.add('jhr-an--paused');
    }

    dismiss(e) {
      if (e && typeof e.preventDefault === 'function') { e.preventDefault(); }
      if (this.classList.contains('added-notification--dismissed')) { return; }
      clearTimeout(this.dismissTimer);
      clearTimeout(this.revealTimer);
      clearTimeout(this._startFallback);
      this.classList.remove('jhr-an--counting', 'jhr-an--paused');
      this.classList.add('added-notification--dismissed');

      /*
       * REMOVE WHEN THE FADE ACTUALLY ENDS, not on a stopwatch.
       *
       * This used to be a flat setTimeout(500). If the main thread was busy at
       * the moment the class landed — which on this page it very often is — the
       * transition started late but the removal fired on schedule, so the card
       * was yanked out mid-fade. That is the "flicks off" the owner reported.
       *
       * transitionend fires when the fade is genuinely finished, however late
       * it started. The timer stays only as a backstop for the cases where the
       * event never arrives at all: a display:none somewhere up the tree, or a
       * browser that drops the event while the tab is hidden. Generous enough
       * not to race the animation it is protecting.
       */
      const done = () => {
        clearTimeout(this._removeTimer);
        this.remove();
      };
      this.addEventListener('transitionend', function onEnd(ev) {
        // Only our own opacity fade — not a child's, and not the transform.
        if (ev.target !== this || ev.propertyName !== 'opacity') { return; }
        this.removeEventListener('transitionend', onEnd);
        done();
      });
      this._removeTimer = setTimeout(done, 900);
    }
  };

  window.customElements.define('added-notification', AddedNotification);
}
